/**
 * O mapa de ameaça: quanto dano cada casa do tabuleiro pode receber.
 *
 * A IA sabia avaliar o ataque que ia dar e o contra-ataque que ia levar de
 * QUEM ela atacou. Não sabia nada sobre os outros — e é aí que a unidade
 * morre. Avançar uma casa a mais para atacar um goblin, parando ao alcance de
 * três lanceiros que ainda não jogaram, é uma troca péssima que a conta antiga
 * dava como ótima: ela via +10 de dano e não via os 24 que voltariam na fase
 * seguinte.
 *
 * Este módulo responde à pergunta que faltava — "se eu parar AQUI, quanto
 * levo?" — somando o dano esperado de todo inimigo que alcança a casa depois
 * de se mover. É a mesma leitura que um jogador humano faz olhando o alcance
 * vermelho do adversário antes de escolher onde parar.
 *
 * O custo é real: para cada inimigo, um campo de deslocamento. Por isso o mapa
 * é calculado UMA vez por unidade que vai agir, e não por casa candidata.
 */
import { forecast } from '../combat.js';
import { buildOccupancy, terrainAt } from '../grid.js';
import { reachableTiles, standableTiles } from '../movement.js';
import { weaponOf } from '../state.js';
import { hasEffect } from '../states.js';
import { positionKey, spanDistance } from '../types.js';
import { combatantOf } from './evaluator.js';
const EMPTY = { damageAt: () => 0, attackersAt: () => 0 };
/**
 * Quanto dano cada casa pode receber dos inimigos que ainda podem jogar.
 *
 * Quem já agiu neste turno entra assim mesmo: a ameaça que interessa é a da
 * PRÓXIMA fase, quando todos voltam a poder agir, e é nela que a unidade vai
 * estar parada. Ignorar quem já jogou faria a IA se enfiar no meio do exército
 * inimigo no fim do turno, que é exatamente o passo suicida que se quer evitar.
 */
export function buildThreatMap(state, content, victim, hostiles) {
    if (hostiles.length === 0)
        return EMPTY;
    const occupancy = buildOccupancy(state.units);
    // Chave numérica, como o resto do motor: `positionKey` devolve número.
    const damage = new Map();
    const count = new Map();
    for (const foe of hostiles) {
        const weapon = weaponOf(content, foe);
        if (!weapon || weapon.heals)
            continue;
        // Paralisado ou dormindo agora é paralisado ou dormindo na próxima fase
        // dele: a condição só sai no FIM do turno de quem a tem. Não ameaça ninguém.
        if (hasEffect(foe.states, 'nao_age'))
            continue;
        const move = content.classes.get(foe.classId)?.move ?? 5;
        const range = reachableTiles(state.map, content, occupancy, foe.position, move, foe.team, foe.size);
        const spots = standableTiles(range, occupancy, foe.id, foe.size);
        /*
         * O dano é medido uma vez por inimigo, e não por casa.
         *
         * O que muda de casa para casa é o terreno de quem apanha — e isso o
         * cálculo aplica na hora, sem refazer a previsão inteira. Refazer custaria
         * um `forecast` por par (casa candidata × inimigo × casa do inimigo), que
         * num mapa de 25×25 com oito inimigos é dezenas de milhares de contas por
         * unidade que vai jogar.
         */
        const attacker = combatantOf(state, content, foe, foe.position);
        const defender = combatantOf(state, content, victim, victim.position);
        const plan = forecast(content, attacker, defender, weapon.range[0]);
        if (!plan.attacker.canAttack)
            continue;
        const expected = plan.attacker.damage * plan.attacker.attacks * (plan.attacker.hit / 100);
        if (expected <= 0)
            continue;
        // As casas que este inimigo consegue ATACAR: as que ele alcança a pé, mais
        // o alcance da arma a partir de cada uma delas.
        const reached = new Set();
        for (const spot of spots) {
            for (const tile of tilesInRange(state, spot, foe.size, victim.size, weapon.range)) {
                const key = positionKey(tile);
                if (reached.has(key))
                    continue;
                reached.add(key);
                damage.set(key, (damage.get(key) ?? 0) + expected);
                count.set(key, (count.get(key) ?? 0) + 1);
            }
        }
    }
    return {
        damageAt: (at) => {
            const raw = damage.get(positionKey(at)) ?? 0;
            if (raw <= 0)
                return 0;
            // O terreno da casa desconta do que se leva nela: um bosque com +20 de
            // esquiva é menos perigoso do que a estrada ao lado, e a IA tem de ver
            // isso ao escolher onde parar.
            const terrain = terrainAt(state.map, content, at);
            const mitigation = 1 - Math.min(0.6, (terrain?.avoid ?? 0) / 100);
            return raw * mitigation - (terrain?.defense ?? 0);
        },
        attackersAt: (at) => count.get(positionKey(at)) ?? 0,
    };
}
/** As casas que um atacante parado em `from` alcança com uma arma de `range`. */
function tilesInRange(state, from, attackerSize, victimSize, range) {
    const min = range[0] ?? 1;
    const max = range[1] ?? 1;
    const out = [];
    for (let dy = -max; dy <= max; dy++) {
        for (let dx = -max; dx <= max; dx++) {
            const tile = { x: from.x + dx, y: from.y + dy };
            if (tile.x < 0 || tile.y < 0 || tile.x >= state.map.width || tile.y >= state.map.height) {
                continue;
            }
            const d = spanDistance(from, attackerSize, tile, victimSize);
            if (d >= min && d <= max)
                out.push(tile);
        }
    }
    return out;
}
//# sourceMappingURL=threat.js.map